import { z } from "zod";
import { StaffMessagingDecisionRecordSchema } from "./patient-consent.js";
import { normalizePatientMessageTemplateText, PATIENT_MESSAGE_EXTENDED_FIELDS, PATIENT_MESSAGE_TEMPLATE_KINDS, PATIENT_MESSAGE_TEMPLATE_LIMITS, PatientMessageTemplateIssueSchema, PatientMessageTemplateKindSchema, PatientMessageTemplateTextSchema, refinePatientMessageTemplate, SmsSegmentsSchema, } from "./patient-message-templates.js";
import { isPatientMessageEmail, normalizePatientMessagePhone, PATIENT_MESSAGE_QUIET_HOURS, PatientMessageChannelSchema, PatientMessageReasonSchema, PatientMessageRecipientMaskSchema, PatientMessageTransportSchema, PatientMessagingTransportModeSchema, patientMessageReasonFits, patientMessageRecipientMaskFits, } from "./patient-messaging.js";
import { PATIENT_MESSAGING_CONSENT_CHANNELS, PatientMessagingConsentSchema, PatientMessagingOfferedChannelsSchema, } from "./patient-messaging-consent.js";
import { IanaTimeZoneSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
/**
 * A clinic's confirmations, reminders and (PA-9) cancellation and change-of-time notices (PA-8 /
 * EXECDEV-215): which channels it sends on, what it sends and when, its quiet hours and the name
 * on its emails, and its message wording
 * (patient-message-templates.ts). Staff routes, on the signed-in member's site:
 *
 * - GET  /v1/scheduling/messaging                   PatientMessagingSettingsResponseSchema
 *                                                   (any staff role)
 * - POST /v1/scheduling/messaging/settings          SavePatientMessagingSettings* (clinic_admin)
 * - POST /v1/scheduling/messaging/templates         PublishPatientMessageTemplate* (clinic_admin)
 * - POST /v1/scheduling/messaging/preview           PatientMessagePreview* (any staff role)
 * - POST /v1/scheduling/messaging/tests             RequestPatientMessageTest* (clinic_admin)
 * - GET  /v1/scheduling/messaging/tests/:testId     PatientMessageTestResponseSchema (its requester)
 * - POST /v1/scheduling/appointments/:id/messaging/decisions and
 *   POST /v1/patients/:id/messaging/decisions       RecordPatientMessagingDecision* (any staff
 *                                                   role; patient-messaging-consent.ts)
 *
 * One clinic-wide cadence is sent on every enabled channel (lead decision K6): a confirmation
 * when a booking is made, and up to three reminders, each 30 minutes to 14 days before the visit.
 * Reminders are planned when the booking is made and planned again when it moves, is cancelled or
 * the settings change; turning a channel off cancels everything queued on it, of every kind.
 */
export const PATIENT_MESSAGING_SETTINGS_LIMITS = {
    /** Reminders per booking (the same offsets on every enabled channel). */
    reminderOffsets: 3,
    /** The earliest and latest a reminder may go before the visit, in minutes (30 min, 14 days). */
    reminderOffsetMinMinutes: 30,
    reminderOffsetMaxMinutes: 20_160,
    /** The From name on the clinic's emails, in UTF-16 code units. */
    emailFromNameMaxLength: 100,
    /** Test messages one staff member may ask for in an hour (scheduling_limit style refusal). */
    testsPerHour: 5,
    /** A test still waiting for the messaging service after this long fails as expired. */
    testExpiryMinutes: 10,
};
// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
/** A clinic-local time of day, "HH:MM" (24-hour clock). */
export const ClinicTimeOfDaySchema = z
    .string()
    .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "Use a time such as 21:00");
/**
 * The From name on the clinic's emails (lead decision K5): editable by clinic_admin, the clinic's
 * name when not set. One line of 1-100 UTF-16 code units, trimmed, with no control or format
 * characters and none of `" < > \ @` (no header tricks, nothing that looks like an address). A
 * name outside ASCII is RFC 2047-encoded by the messaging service.
 */
export const PatientMessageEmailFromNameSchema = z
    .string()
    .min(1, "Enter the name patients see")
    .max(PATIENT_MESSAGING_SETTINGS_LIMITS.emailFromNameMaxLength, `Keep the name to ${PATIENT_MESSAGING_SETTINGS_LIMITS.emailFromNameMaxLength} characters or fewer`)
    .refine((value) => value === value.trim(), "The name cannot start or end with a space")
    .refine((value) => !/[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u.test(value), "Keep the name on one line, without special characters")
    .refine((value) => !/["<>\\@]/.test(value), 'The name cannot contain " < > \\ or @');
const ReminderOffsetSchema = z
    .number()
    .int()
    .min(PATIENT_MESSAGING_SETTINGS_LIMITS.reminderOffsetMinMinutes, "A reminder goes at least 30 minutes before the visit")
    .max(PATIENT_MESSAGING_SETTINGS_LIMITS.reminderOffsetMaxMinutes, "A reminder goes at most 14 days before the visit");
const toggle = z.object({ enabled: z.boolean() }).strict();
export const PATIENT_DOCUMENT_MESSAGING_DEFAULTS = {
    sick_note: false,
    school_note: false,
    return_to_work: false,
    general: false,
    handout: false,
};
export const PatientDocumentMessagingSettingsSchema = z
    .object({
    sick_note: z.boolean(),
    school_note: z.boolean(),
    return_to_work: z.boolean(),
    general: z.boolean(),
    handout: z.boolean(),
})
    .strict();
/**
 * The clinic's messaging settings: the strict write (every key required) and what reads return.
 *
 * - sms / email: whether the clinic sends on the channel. Turning one on needs its sender set up
 *   (messaging_sender_missing otherwise); turning one off cancels everything queued on it.
 *   email.fromName: null sends as the clinic's name.
 * - confirmation: sent when a booking is made (online, or by staff with "Send confirmation").
 *   Turning it on later does not confirm bookings already made; turning it off does not cancel
 *   confirmations already queued.
 * - reminders: offsetsMinutes, before the visit, unique, largest (earliest) first; at least one
 *   while enabled, kept while disabled.
 * - cancellation: from PA-9 (lead decision L6), a cancellation notice whenever a booking is
 *   cancelled, by the patient or by staff. reschedule: a change-of-time notice whenever a
 *   booking moves to another time, by the patient or by staff. Confirming a booking or marking a
 *   no-show sends neither. Like a confirmation, turning one on later does not notify changes
 *   already made. Both start off (the key and its default date from PA-8, so this write is
 *   unchanged).
 * - quietHours: on the clinic's clock, start to end (it may cross midnight; start and end
 *   differ). A reminder due inside waits until the end, or goes at the start when the end is less
 *   than 15 minutes before the visit; two reminders moved to the same time are sent once.
 *   Confirmations and desk-initiated link messages are never held.
 */
export const PatientMessagingSettingsSchema = z
    .object({
    sms: toggle,
    email: z
        .object({ enabled: z.boolean(), fromName: PatientMessageEmailFromNameSchema.nullable() })
        .strict(),
    documents: PatientDocumentMessagingSettingsSchema,
    confirmation: toggle,
    reminders: z
        .object({
        enabled: z.boolean(),
        offsetsMinutes: z
            .array(ReminderOffsetSchema)
            .max(PATIENT_MESSAGING_SETTINGS_LIMITS.reminderOffsets),
    })
        .strict()
        .superRefine((value, context) => {
        if (value.enabled && value.offsetsMinutes.length === 0) {
            context.addIssue({
                code: "custom",
                message: "Add at least one reminder time",
                path: ["offsetsMinutes"],
            });
        }
        if (new Set(value.offsetsMinutes).size !== value.offsetsMinutes.length) {
            context.addIssue({
                code: "custom",
                message: "Each reminder goes at a different time",
                path: ["offsetsMinutes"],
            });
        }
        else if (value.offsetsMinutes.some((offset, index) => index > 0 && offset > (value.offsetsMinutes[index - 1] ?? offset))) {
            context.addIssue({
                code: "custom",
                message: "List the earliest reminder first",
                path: ["offsetsMinutes"],
            });
        }
    }),
    cancellation: toggle,
    reschedule: toggle,
    quietHours: z
        .object({ enabled: z.boolean(), start: ClinicTimeOfDaySchema, end: ClinicTimeOfDaySchema })
        .strict()
        .refine((value) => value.start !== value.end, {
        message: "Quiet hours start and end at different times",
        path: ["end"],
    }),
})
    .strict();
/** Reads normalize old rows during rollout; writes still require all switches. */
export const PatientMessagingSettingsReadSchema = PatientMessagingSettingsSchema.extend({
    documents: PatientDocumentMessagingSettingsSchema.default({
        ...PATIENT_DOCUMENT_MESSAGING_DEFAULTS,
    }),
});
/**
 * What a clinic starts with: nothing sent (every switch off, a 24-hour reminder ready to turn
 * on), quiet hours on from 21:00 to 08:00 (PATIENT_MESSAGE_QUIET_HOURS), emails from the clinic's
 * name. The store seeds the same value.
 */
export const PATIENT_MESSAGING_DEFAULT_SETTINGS = {
    sms: { enabled: false },
    email: { enabled: false, fromName: null },
    documents: { ...PATIENT_DOCUMENT_MESSAGING_DEFAULTS },
    confirmation: { enabled: false },
    reminders: { enabled: false, offsetsMinutes: [1440] },
    cancellation: { enabled: false },
    reschedule: { enabled: false },
    quietHours: {
        enabled: true,
        start: PATIENT_MESSAGE_QUIET_HOURS.start,
        end: PATIENT_MESSAGE_QUIET_HOURS.end,
    },
};
// ---------------------------------------------------------------------------
// Whether messaging is offered (lead decision K3)
// ---------------------------------------------------------------------------
/**
 * One channel, as the admin page shows it, most basic first:
 *
 * - sender_missing: no sender is set up for the clinic (the operator's binding: a Twilio
 *   Messaging Service, or an SES identity). The switch cannot be turned on.
 * - off: the clinic turned the channel off.
 * - delivery_unavailable: the messaging service is not running in a mode that delivers (off,
 *   blocked, or not seen yet): in production until live sending is allowed.
 * - ready: patients are asked, and messages go out.
 */
export const PatientMessagingChannelReadinessSchema = z.enum([
    "ready",
    "off",
    "sender_missing",
    "delivery_unavailable",
]);
/**
 * Transports that deliver. simulated counts because the lane runs simulated only with synthetic
 * data (SYNTHETIC_DATA_ONLY=true, no PHI flags), so a synthetic environment offers messaging it
 * really "sends" to its dev sink; off and blocked never do.
 */
export const PATIENT_MESSAGING_DELIVERING_TRANSPORTS = new Set(["live", "simulated"]);
/** A channel's readiness (lead decision K3: enabled AND a sender AND a transport that delivers). */
export function patientMessagingChannelReadiness(inputs) {
    if (!inputs.senderReady)
        return "sender_missing";
    if (!inputs.enabled)
        return "off";
    if (inputs.transport === null || !PATIENT_MESSAGING_DELIVERING_TRANSPORTS.has(inputs.transport)) {
        return "delivery_unavailable";
    }
    return "ready";
}
/**
 * The channels offered now, texts before emails. Messaging is offered exactly when this is not
 * empty: only then is the patient's messaging line shown, and only then may staff record an
 * agreement, so nobody is asked for consent the clinic cannot honour. The store's
 * private.scheduling_messaging_offered applies the same rule.
 */
export function patientMessagingOfferedChannels(channels) {
    return PATIENT_MESSAGING_CONSENT_CHANNELS.filter((channel) => patientMessagingChannelReadiness(channels[channel]) === "ready");
}
// ---------------------------------------------------------------------------
// Templates as stored
// ---------------------------------------------------------------------------
export const PatientMessageTemplateVersionNumberSchema = z
    .number()
    .int()
    .positive()
    .max(2_147_483_647);
/**
 * default: the shipped wording (PATIENT_MESSAGE_DEFAULT_TEMPLATES), seeded or restored; clinic:
 * wording the clinic published.
 */
export const PatientMessageTemplateSourceSchema = z.enum(["default", "clinic"]);
/** One published version of a series, as staff read it. Immutable; every stored rule holds. */
export const PatientMessageTemplateVersionSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    version: PatientMessageTemplateVersionNumberSchema,
    ...PatientMessageTemplateTextSchema.shape,
    /** sha256 of {kind, channel, subject, body}: what each message sent with it records. */
    contentHash: Sha256Schema,
    source: PatientMessageTemplateSourceSchema,
    publishedAt: IsoDateTimeSchema,
    /** Who published it (display name, or "A staff member"); null for the seeded version. */
    publishedByName: z.string().trim().min(1).max(200).nullable(),
})
    .strict()
    .superRefine((value, context) => refinePatientMessageTemplate(value, context, { stored: true }));
/**
 * One series: its current version and earlier ones, newest first (the oldest are left out past
 * PATIENT_MESSAGE_TEMPLATE_LIMITS.templateHistory). The default text for "Restore default text"
 * is PATIENT_MESSAGE_DEFAULT_TEMPLATES[kind][channel].
 */
export const PatientMessageTemplateSeriesSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    current: PatientMessageTemplateVersionSchema,
    history: z
        .array(PatientMessageTemplateVersionSchema)
        .max(PATIENT_MESSAGE_TEMPLATE_LIMITS.templateHistory),
})
    .strict()
    .superRefine((value, context) => {
    for (const [path, version] of [
        [["current"], value.current],
        ...value.history.map((entry, index) => [["history", index], entry]),
    ]) {
        if (version.kind !== value.kind || version.channel !== value.channel) {
            context.addIssue({
                code: "custom",
                message: "Every version belongs to its series",
                path: [...path, "kind"],
            });
        }
    }
    let newer = value.current.version;
    for (const [index, earlier] of value.history.entries()) {
        if (earlier.version >= newer) {
            context.addIssue({
                code: "custom",
                message: "Earlier versions are listed newest first, below the current version",
                path: ["history", index, "version"],
            });
        }
        newer = earlier.version;
    }
});
/** Every series in the order the admin page lists them: each kind, texts before emails. */
export const PATIENT_MESSAGE_TEMPLATE_SERIES = PATIENT_MESSAGE_TEMPLATE_KINDS.flatMap((kind) => PATIENT_MESSAGING_CONSENT_CHANNELS.map((channel) => ({ kind, channel })));
// ---------------------------------------------------------------------------
// The admin read and saves
// ---------------------------------------------------------------------------
const E164 = /^\+[1-9][0-9]{6,14}$/;
/**
 * The clinic's senders, read-only (lead decision K5): the operator provisions the texting number
 * (PA-7's binding) and the email address; only the email's From name is the clinic's to change.
 */
export const PatientMessagingSendersSchema = z
    .object({
    sms: z
        .object({
        /** A sender is set up and enabled for texts. */
        ready: z.boolean(),
        /** The number texts come from (E.164), when the operator recorded it. */
        number: z.string().regex(E164).nullable(),
    })
        .strict(),
    email: z
        .object({
        ready: z.boolean(),
        fromAddress: z
            .string()
            .max(254)
            .refine(isPatientMessageEmail, "An email address")
            .nullable(),
        /** The name patients see: the clinic's From name setting, or the clinic's name. */
        fromName: z.string().trim().min(1).max(240),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    if (!value.sms.ready && value.sms.number !== null) {
        context.addIssue({
            code: "custom",
            message: "Only a texting sender that is set up has a number",
            path: ["sms", "number"],
        });
    }
    if (value.email.ready !== (value.email.fromAddress !== null)) {
        context.addIssue({
            code: "custom",
            message: "An email sender that is set up has its address, and only then",
            path: ["email", "fromAddress"],
        });
    }
});
/**
 * `GET /v1/scheduling/messaging`: the clinic's settings, senders, readiness per channel and
 * every template series. Any staff role reads it; only clinic_admin saves or publishes.
 */
export const PatientMessagingSettingsResponseSchema = z
    .object({
    /** The settings version a save names as expectedVersion (1 for the seeded defaults). */
    version: z.number().int().positive().max(2_147_483_647),
    settings: PatientMessagingSettingsReadSchema,
    /** When an admin last saved; null while the seeded defaults stand. */
    updatedAt: IsoDateTimeSchema.nullable(),
    clinicName: z.string().trim().min(1).max(240),
    /** Times on the admin page (quiet hours) are on this clock. */
    timeZone: IanaTimeZoneSchema,
    senders: PatientMessagingSendersSchema,
    /** The messaging lane's latest transport for the tenant; null when no lane has reported. */
    transport: PatientMessagingTransportModeSchema.nullable(),
    channels: z
        .object({
        sms: PatientMessagingChannelReadinessSchema,
        email: PatientMessagingChannelReadinessSchema,
    })
        .strict(),
    offeredChannels: PatientMessagingOfferedChannelsSchema,
    templates: z
        .array(PatientMessageTemplateSeriesSchema)
        .length(PATIENT_MESSAGE_TEMPLATE_SERIES.length),
})
    .strict()
    .superRefine((value, context) => {
    const inputs = {
        sms: {
            enabled: value.settings.sms.enabled,
            senderReady: value.senders.sms.ready,
            transport: value.transport,
        },
        email: {
            enabled: value.settings.email.enabled,
            senderReady: value.senders.email.ready,
            transport: value.transport,
        },
    };
    for (const channel of PATIENT_MESSAGING_CONSENT_CHANNELS) {
        if (value.channels[channel] !== patientMessagingChannelReadiness(inputs[channel])) {
            context.addIssue({
                code: "custom",
                message: "A channel's readiness follows its switch, its sender and the transport",
                path: ["channels", channel],
            });
        }
    }
    if (value.offeredChannels.join() !== patientMessagingOfferedChannels(inputs).join()) {
        context.addIssue({
            code: "custom",
            message: "The offered channels are exactly the ready ones",
            path: ["offeredChannels"],
        });
    }
    PATIENT_MESSAGE_TEMPLATE_SERIES.forEach((series, index) => {
        const listed = value.templates[index];
        if (listed !== undefined &&
            (listed.kind !== series.kind || listed.channel !== series.channel)) {
            context.addIssue({
                code: "custom",
                message: "Every series is listed once, in order",
                path: ["templates", index],
            });
        }
    });
});
/**
 * `POST /v1/scheduling/messaging/settings` (clinic_admin): the whole settings against
 * expectedVersion. Refusals: a stale version is a 409 with messaging_settings_changed (the page
 * keeps the admin's choices and reloads); turning on a channel with no sender is a 409 with
 * messaging_sender_missing; invalid settings are a 422. Saving identical settings saves nothing.
 * A save plans the clinic's future bookings again in the same transaction.
 */
export const SavePatientMessagingSettingsRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive().max(2_147_483_647),
    settings: PatientMessagingSettingsSchema,
})
    .strict();
/** What a save did to messages already planned: cancelled (still queued) and newly planned. */
export const PatientMessagingSaveImpactSchema = z
    .object({ cancelled: z.number().int().min(0), planned: z.number().int().min(0) })
    .strict();
export const SavePatientMessagingSettingsResponseSchema = z
    .object({
    /** False when the settings matched the current ones, so nothing was saved. */
    saved: z.boolean(),
    messaging: PatientMessagingSettingsResponseSchema,
    impact: PatientMessagingSaveImpactSchema,
})
    .strict()
    .refine((value) => value.saved || (value.impact.cancelled === 0 && value.impact.planned === 0), {
    message: "A save that saved nothing changed no message",
    path: ["impact"],
});
const draftTextShape = {
    subject: z
        .string()
        .max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength)
        .transform(normalizePatientMessageTemplateText)
        .nullable(),
    body: z
        .string()
        .max(PATIENT_MESSAGE_TEMPLATE_LIMITS.draftMaxLength)
        .transform(normalizePatientMessageTemplateText),
};
/**
 * `POST /v1/scheduling/messaging/templates` (clinic_admin): publishes version expectedVersion + 1
 * of the kind and channel's series. Line breaks are normalized and the ends trimmed, then every
 * rule applies (an unknown field is a 422 with the field's path, before the store is asked).
 * Text identical to the current version publishes nothing; text identical to the default is
 * stored with source default. A stale expectedVersion is a 409 with message_template_changed.
 * Messages already sent keep the version they used.
 */
export const PublishPatientMessageTemplateRequestSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    expectedVersion: PatientMessageTemplateVersionNumberSchema,
    ...draftTextShape,
})
    .strict()
    .superRefine((value, context) => refinePatientMessageTemplate(value, context, { filledLength: true }));
export const PublishPatientMessageTemplateResponseSchema = z
    .object({
    /** False when the text matched the current version, so nothing was published. */
    published: z.boolean(),
    messaging: PatientMessagingSettingsResponseSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Preview and test messages
// ---------------------------------------------------------------------------
/**
 * `POST /v1/scheduling/messaging/preview` (any staff role): a draft rendered by the server with
 * sample details (a sample patient and visit at this clinic, sample links) and this clinic's
 * name, phone and address. Never a real booking. The draft is not refused: its issues come back
 * in the answer (200), so the editor shows them inline.
 */
export const PatientMessagePreviewRequestSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    ...PatientMessageTemplateTextSchema.shape,
})
    .strict();
export const PatientMessagePreviewResponseSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    /** Everything that would stop a publish; rendered is null while there are any. */
    issues: z.array(PatientMessageTemplateIssueSchema).max(PATIENT_MESSAGE_TEMPLATE_LIMITS.issues),
    /** Extended fields the draft uses: the clinic's messaging notice must cover them. */
    cautions: z
        .array(z.enum(PATIENT_MESSAGE_EXTENDED_FIELDS))
        .max(PATIENT_MESSAGE_EXTENDED_FIELDS.length),
    /** The sample message with every field filled and the footer appended. */
    rendered: z
        .object({
        subject: z
            .string()
            .max(PATIENT_MESSAGE_TEMPLATE_LIMITS.emailSubjectRenderedMaxLength)
            .nullable(),
        body: z.string().max(PATIENT_MESSAGE_TEMPLATE_LIMITS.emailBodyRenderedMaxLength),
    })
        .strict()
        .nullable(),
    /** A rendered text's parts and encoding ("148 characters · 1 text · standard characters"). */
    sms: SmsSegmentsSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.rendered === null) !== value.issues.length > 0) {
        context.addIssue({
            code: "custom",
            message: "A draft is rendered exactly when it has no issues",
            path: ["rendered"],
        });
    }
    if (value.rendered !== null &&
        (value.rendered.subject === null) !== (value.channel === "sms")) {
        context.addIssue({
            code: "custom",
            message: "An email has a subject and a text has none",
            path: ["rendered", "subject"],
        });
    }
    if ((value.sms !== null) !== (value.channel === "sms" && value.rendered !== null)) {
        context.addIssue({
            code: "custom",
            message: "A rendered text counts its parts; nothing else does",
            path: ["sms"],
        });
    }
});
/**
 * `POST /v1/scheduling/messaging/tests` (clinic_admin): a test message with sample details,
 * through the messaging service like any message: in development and on synthetic staging the
 * simulated transport (the dev sink, status simulated), never a provider unless the lane's live
 * boundary allows it. A text goes to the typed phone (a North American number, kept only until
 * the test finishes); an email goes to the signed-in staff member's own address. draft null tests
 * the series' current version. At most PATIENT_MESSAGING_SETTINGS_LIMITS.testsPerHour per staff
 * member (a 422 with message_test_limit_reached); a clinic with no sender for the channel is a
 * 409 with messaging_sender_missing, and while no messaging service delivers (transport off,
 * blocked or not reporting) a 409 with messaging_not_offered. 202 with the queued test.
 */
export const RequestPatientMessageTestSchema = z
    .object({
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    draft: z.object(draftTextShape).strict().nullable(),
    /** Texts only: where to send it. */
    phone: z.string().trim().min(1).max(40).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.channel === "sms") {
        if (value.phone === undefined || normalizePatientMessagePhone(value.phone) === null) {
            context.addIssue({
                code: "custom",
                message: "Enter a North American mobile number",
                path: ["phone"],
            });
        }
    }
    else if (value.phone !== undefined) {
        context.addIssue({
            code: "custom",
            message: "A test email goes to your own address",
            path: ["phone"],
        });
    }
    if (value.draft !== null) {
        refinePatientMessageTemplate({ kind: value.kind, channel: value.channel, ...value.draft }, context, { filledLength: true }, ["draft"]);
    }
});
/**
 * A test message's state. queued and sending wait for the messaging service; simulated: the
 * simulated transport took it (nothing left Bob Doc); sent: a provider accepted it; failed:
 * with a reason (expired after PATIENT_MESSAGING_SETTINGS_LIMITS.testExpiryMinutes); stopped:
 * the number replied STOP to this clinic.
 */
export const PatientMessageTestStatusSchema = z.enum([
    "queued",
    "sending",
    "simulated",
    "sent",
    "failed",
    "stopped",
]);
const FINISHED_TEST_STATUSES = new Set([
    "simulated",
    "sent",
    "failed",
    "stopped",
]);
/** A test message as its requester reads it. Never the body or the full phone or address. */
export const PatientMessageTestSchema = z
    .object({
    id: z.string().uuid(),
    kind: PatientMessageTemplateKindSchema,
    channel: PatientMessageChannelSchema,
    /** The series version tested, or null for a draft. */
    templateVersion: PatientMessageTemplateVersionNumberSchema.nullable(),
    status: PatientMessageTestStatusSchema,
    reason: PatientMessageReasonSchema.nullable(),
    transport: PatientMessageTransportSchema.nullable(),
    recipientMasked: PatientMessageRecipientMaskSchema,
    requestedAt: IsoDateTimeSchema,
    finishedAt: IsoDateTimeSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    const reasonFits = value.status === "failed"
        ? patientMessageReasonFits("failed", value.channel, value.reason)
        : value.status === "stopped"
            ? value.reason === "opted_out"
            : value.reason === null;
    if (!reasonFits) {
        context.addIssue({
            code: "custom",
            message: "A failed or stopped test says why, and only those",
            path: ["reason"],
        });
    }
    const transport = value.status === "simulated"
        ? value.transport === "simulated"
        : value.status === "sent"
            ? value.transport === "live"
            : value.status === "queued"
                ? value.transport === null
                : true;
    if (!transport) {
        context.addIssue({
            code: "custom",
            message: "A test says how it went out, once it did",
            path: ["transport"],
        });
    }
    if (FINISHED_TEST_STATUSES.has(value.status) !== (value.finishedAt !== null)) {
        context.addIssue({
            code: "custom",
            message: "A finished test says when it finished, and only a finished one",
            path: ["finishedAt"],
        });
    }
    if (!patientMessageRecipientMaskFits(value.channel, value.recipientMasked)) {
        context.addIssue({
            code: "custom",
            message: "A text shows a masked phone number and an email a masked address",
            path: ["recipientMasked"],
        });
    }
});
/** The answer to a test request (202) and to `GET /v1/scheduling/messaging/tests/:testId`. */
export const PatientMessageTestResponseSchema = z
    .object({ test: PatientMessageTestSchema })
    .strict();
// ---------------------------------------------------------------------------
// Staff recording a decision: the answer
// ---------------------------------------------------------------------------
/**
 * The answer to RecordPatientMessagingDecisionRequestSchema: the decision (created false on an
 * exact retry) and the target's channel states after it (the booking's, or the chart's).
 */
export const RecordPatientMessagingDecisionResponseSchema = z
    .object({
    created: z.boolean(),
    decision: StaffMessagingDecisionRecordSchema,
    consent: PatientMessagingConsentSchema,
    offeredChannels: PatientMessagingOfferedChannelsSchema,
})
    .strict();
//# sourceMappingURL=patient-messaging-settings.js.map